// The visitor's product analytics decision, shared by the banner, Settings, the analytics wrapper and the server
// sync (docs/analytics.md). Rules live in ./consent (pure and unit tested); this keeps the current answer in one place.
import { create } from 'zustand'
import {
  ANALYTICS_CONSENT_KEY,
  ANALYTICS_CONSENT_VERSION,
  decide,
  markVisitChoice,
  parseChoice,
  privacySignal,
  readChoice,
  readGeo,
  writeChoice,
  type ConsentSource,
  type Decision,
  type Geo,
  type PrivacySignal,
  type StoredChoice,
} from './consent'

type ConsentState = {
  geo: Geo
  signal: PrivacySignal
  stored: StoredChoice | null
  decision: Decision
  /** the choices were opened from "Analytics choices" (the banner never opens by itself) */
  reopened: boolean
  /** the signed in person (their user id), null when signed out, undefined while not known yet */
  viewer: string | null | undefined
  /** the time of the account's analytics answer this browser knows (null: none) */
  serverAt: string | null
  /** Accept, Decline or Opt out in the banner, or the Settings switch */
  choose: (granted: boolean, source: ConsentSource) => void
  /** OK in the choices where analytics are on by default: they stay on */
  acknowledge: () => void
  /** the signed in person's answer from the server (another device); this browser's own answer is kept aside */
  adopt: (choice: StoredChoice, userId?: string | null) => void
  /** the account now holds this browser's answer `answered`, under the account's time `at`: no longer pending */
  settle: (answered: Pick<StoredChoice, 'state' | 'source' | 'at'>, at: string) => void
  /** who is signed in (null: nobody), from /api/auth/me; a session that ended gives the browser its own answer back */
  setViewer: (viewer: string | null, serverAt?: string | null) => void
  /** sign out: an answer adopted from the account or given while signed in gives way to this browser's own answer
   *  again, so the next person on a shared computer is not tracked on the last one's yes */
  signOut: () => void
  reopen: () => void
  /** hides the reopened banner without changing the answer */
  close: () => void
  /** reads location, signals and storage again (another tab changed the answer; tests) */
  refresh: () => void
}

/** The decision for `viewer`: a signed in person's answer (adopted from their account or given here while signed in) is
 *  in force only once that person is known to be signed in here. Until /api/auth/me answers, analytics wait, off and
 *  without a banner (a failed call keeps them off), so the next person on a shared computer never runs on the last
 *  one's yes (re-audit minor 9); for anyone else setViewer puts the browser's own answer back. */
function decideFor(geo: Geo, signal: PrivacySignal, stored: StoredChoice | null, viewer: string | null | undefined): Decision {
  const belongs = !!stored && (!!stored.adopted || !!stored.owner)
  if (!belongs || stored?.owner === viewer) return decide(geo, signal, stored)
  const waiting = decide(geo, signal, null)
  return viewer === undefined && !signal ? { ...waiting, allowed: false, reason: 'pending' } : waiting
}

function snapshot(viewer: string | null | undefined): Pick<ConsentState, 'geo' | 'signal' | 'stored' | 'decision'> {
  const geo = readGeo()
  const signal = privacySignal()
  const stored = readChoice()
  return { geo, signal, stored, decision: decideFor(geo, signal, stored, viewer) }
}

/** The browser's own answer is set aside the first time a signed in person's answer replaces it (adopted from their
 *  account or given here), and comes back when the session ends. */
function asideOwn(current: StoredChoice | null): void {
  keepDeviceChoice(current && !current.adopted && !current.owner ? current : null)
}

/** Puts the browser's own answer back when the one in force belongs to a signed in person other than `viewer` (true
 *  then); a kept answer with nothing of a person in force is a leftover and goes. */
function ownAnswerBack(stored: StoredChoice | null, viewer: string | null): boolean {
  const belongs = !!stored && (!!stored.adopted || !!stored.owner)
  if (belongs && stored?.owner === viewer) return false // still theirs: the browser's answer keeps waiting
  const kept = takeDeviceChoice()
  if (!belongs) return false
  writeChoice(kept ?? null)
  return true
}

export const useConsentStore = create<ConsentState>((set, get) => {
  const enter = (viewer: string | null, serverAt: string | null) => {
    if (ownAnswerBack(get().stored, viewer)) set({ ...snapshot(viewer), viewer, serverAt, reopened: false })
    else set({ viewer, serverAt, decision: decideFor(get().geo, get().signal, get().stored, viewer) })
  }
  return {
    ...snapshot(undefined),
    reopened: false,
    viewer: undefined,
    serverAt: null,
    choose: (granted, source) => {
      const { viewer, serverAt } = get()
      if (viewer) asideOwn(get().stored)
      const stored: StoredChoice = { state: granted ? 'granted' : 'denied', at: new Date().toISOString(),
        version: ANALYTICS_CONSENT_VERSION, source, pending: true, ...(viewer ? { owner: viewer, basis: serverAt } : {}) }
      writeChoice(stored)
      // given while nobody is signed in: it goes to the account of whoever signs in during this visit only
      markVisitChoice(viewer ? null : stored.at)
      set({ stored, decision: decideFor(get().geo, get().signal, stored, viewer), reopened: false })
    },
    acknowledge: () => get().choose(true, 'notice'),
    adopt: (choice, userId) => {
      asideOwn(get().stored)
      const owner = userId ?? get().viewer ?? undefined
      const stored: StoredChoice = { state: choice.state, at: choice.at, version: choice.version, source: choice.source,
        adopted: true, ...(owner ? { owner } : {}) }
      writeChoice(stored)
      set({ stored, decision: decideFor(get().geo, get().signal, stored, owner ?? get().viewer) })
    },
    settle: (answered, at) => {
      const s = get().stored
      if (!s || s.state !== answered.state || s.source !== answered.source || s.at !== answered.at) return
      const stored: StoredChoice = { state: s.state, at, version: s.version, source: s.source,
        ...(s.adopted ? { adopted: true } : {}), ...(s.owner ? { owner: s.owner } : {}) }
      writeChoice(stored)
      set({ stored })
    },
    setViewer: (viewer, serverAt = null) => {
      const prev = get()
      if (prev.viewer === viewer) {
        if (prev.serverAt !== serverAt) set({ serverAt })
        return
      }
      enter(viewer, serverAt)
    },
    signOut: () => enter(null, null),
    reopen: () => set({ reopened: true }),
    close: () => set({ reopened: false }),
    refresh: () => set({ ...snapshot(get().viewer), reopened: false }),
  }
})

/** This browser's own answer while a signed in person's answer is in force (null: it had none), until the session
 *  ends. */
export const DEVICE_CHOICE_KEY = 'mgs-analytics-consent-device'

function keepDeviceChoice(choice: StoredChoice | null): void {
  try {
    if (globalThis.localStorage?.getItem(DEVICE_CHOICE_KEY) === null) {
      globalThis.localStorage?.setItem(DEVICE_CHOICE_KEY, JSON.stringify(choice))
    }
  } catch {
    /* storage blocked: the browser's answer cannot come back, so the region's default applies after sign out */
  }
}

/** The kept answer (null when the browser had none) and forgets it; undefined when nothing was kept. */
function takeDeviceChoice(): StoredChoice | null | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(DEVICE_CHOICE_KEY)
    if (raw === null || raw === undefined) return undefined
    globalThis.localStorage?.removeItem(DEVICE_CHOICE_KEY)
    return parseChoice(JSON.parse(raw) as unknown)
  } catch {
    return undefined
  }
}

/** Analytics run now: the decision allows them (on by default where consent does not come first, with no banner:
 *  the Privacy Policy explains it, MG 2026-10-07). */
export function effectiveAllowed(s: Pick<ConsentState, 'decision'>): boolean {
  return s.decision.allowed
}

/** The current decision outside React (the analytics wrapper). */
export function analyticsAllowed(): boolean {
  return effectiveAllowed(useConsentStore.getState())
}

// Another tab answered: follow it here too.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === ANALYTICS_CONSENT_KEY) useConsentStore.getState().refresh()
  })
}
